import type { WebGLRenderer } from 'three';
import { num, pick, query as q } from './params';
import { sharedUniforms } from '../render/materials/toon';
import type { BiomeName, TimeOfDay } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import type { WeatherKind } from '../render/vfx/weather';
import { createLookdev } from '../scenes/lookdev';

/**
 * Dev routes (06 §8). Everything is reachable by URL so an agent can drive it:
 *   ?scene=lookdev&biome=arctic|desert|temperate&tod=dawn|day|dusk|night
 *   &weather=clear|snow|blizzard|rain|sandstorm|thunderstorm&seed=1&pan=1&freeze=1&t=12.5
 *   &lock=0.85&dither=0.07&haze=0.1&outlines=0&hud=0&pitch=45&ppm=18
 */
// Debug API for agent-driven checks (06 §8).
declare global {
  interface Window {
    __game: {
      capture(): string;
      info(): unknown;
      stability(dx: number, dz: number): unknown;
      save(name: string, scale?: number): Promise<string>;
      paletteCheck(): unknown;
    };
  }
}

export function startLookdev(canvas: HTMLCanvasElement, renderer: WebGLRenderer): void {
const biome = pick<BiomeName>('biome', ['arctic', 'desert', 'temperate'], 'arctic');
const tod = pick<TimeOfDay>('tod', ['dawn', 'day', 'dusk', 'night'], 'day');
const weather = pick<WeatherKind>('weather', ['clear', 'snow', 'blizzard', 'rain', 'sandstorm', 'thunderstorm'], 'clear');
const frozen = q.get('freeze') === '1';
const frozenTime = num('t', 0);

const world = createLookdev({ biome, tod, weather, seed: num('seed', 1), pan: q.get('pan') === '1', pitch: num('pitch', 45), ppm: num('ppm', 18) });
const pipeline = new RenderPipeline(renderer, world.palette.buildLut(), world.palette.color('ink.0'));
const activeGrade = { ...world.grade, hazeAmount: num('haze', world.grade.hazeAmount) };
pipeline.setGrade(activeGrade, world.palette.haze);
pipeline.setPlayerRim(tod === 'night' ? world.palette.hex('paper.0') : null);
pipeline.settings.paletteLock = num('lock', pipeline.settings.paletteLock);
pipeline.settings.ditherAmp = num('dither', pipeline.settings.ditherAmp);
pipeline.settings.outlines = q.get('outlines') !== '0';



const dev = document.getElementById('dev');
if (q.get('hud') === '0' && dev) dev.style.display = 'none';
let last = performance.now();
let frames = 0;
let fpsTime = 0;
let fps = 0;
const start = performance.now();

function frame(now: number): void {
  const dt = Math.min(0.1, (now - last) / 1000);
  last = now;
  const time = frozen ? frozenTime : (now - start) / 1000;
  sharedUniforms.uTime.value = time;
  world.update(time, dt);
  renderer.info.reset();
  pipeline.render(world.scene, world.camera);

  frames++;
  fpsTime += dt;
  if (fpsTime >= 0.5) {
    fps = Math.round(frames / fpsTime);
    frames = 0;
    fpsTime = 0;
    const info = renderer.info.render;
    if (dev) {
      dev.textContent = `${biome} ${tod} ${weather}  x${pipeline.integerScale}  ${fps} fps  ${info.calls} calls  ${info.triangles} tris`;
    }
  }
  requestAnimationFrame(frame);
}
requestAnimationFrame(frame);

window.__game = {
  /** PNG data URL of the current frame (renders synchronously first so the buffer is valid). */
  capture() {
    pipeline.render(world.scene, world.camera);
    return canvas.toDataURL('image/png');
  },
  info() {
    return { biome, tod, weather, fps, scale: pipeline.integerScale, render: renderer.info.render };
  },
  /**
   * Save the exact internal frame (640x360, border cropped) as docs/lookdev/<name>.png via
   * the dev server, blown up by an integer `scale` with hard pixel edges.
   */
  async save(name: string, scale = 2) {
    pipeline.render(world.scene, world.camera);
    const src = pipeline.readPixels();
    const W = 642;
    const w = 640;
    const h = 360;
    const small = document.createElement('canvas');
    small.width = w;
    small.height = h;
    const ctx = small.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    const image = ctx.createImageData(w, h);
    for (let y = 0; y < h; y++) {
      // readPixels is bottom-up; skip the 1-texel border.
      const srcRow = (h - y) * W + 1;
      for (let x = 0; x < w; x++) {
        const i = (srcRow + x) * 4;
        const o = (y * w + x) * 4;
        image.data[o] = src[i] ?? 0;
        image.data[o + 1] = src[i + 1] ?? 0;
        image.data[o + 2] = src[i + 2] ?? 0;
        image.data[o + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
    const big = document.createElement('canvas');
    big.width = w * scale;
    big.height = h * scale;
    const bctx = big.getContext('2d');
    if (!bctx) throw new Error('no 2d context');
    bctx.imageSmoothingEnabled = false;
    bctx.drawImage(small, 0, 0, big.width, big.height);
    const res = await fetch(`/__lookdev/save?name=${encodeURIComponent(name)}`, { method: 'POST', body: big.toDataURL('image/png') });
    return res.text();
  },
  /**
   * Palette compliance (05 §12): with lock=1 and haze=0 and a neutral grade, every pixel
   * must be a palette colour. Returns the number of distinct colours on screen.
   */
  paletteCheck() {
    pipeline.render(world.scene, world.camera);
    const px = pipeline.readPixels();
    const seen = new Set<number>();
    for (let i = 0; i < px.length; i += 4) seen.add(((px[i] ?? 0) << 16) | ((px[i + 1] ?? 0) << 8) | (px[i + 2] ?? 0));
    const palette = new Set(world.palette.all().map(([r, g, b]) => (r << 16) | (g << 8) | b));
    let offPalette = 0;
    for (const c of seen) if (!palette.has(c)) offPalette++;
    return { distinctColours: seen.size, paletteSize: palette.size, offPalette };
  },
  /**
   * Scroll-stability check (05 §12): render at the origin and again with the camera moved
   * by (dx, dz) metres, then compare the overlapping pixels after undoing the whole-texel
   * shift. Static, world-anchored imagery must match exactly. Use with freeze=1.
   */
  stability(dx: number, dz: number) {
    const shoot = (x: number, z: number) => {
      world.setFocus(x, z);
      world.update(frozenTime, 0);
      pipeline.render(world.scene, world.camera);
      return { pixels: pipeline.readPixels(), tx: world.camera.camTexel.x, ty: world.camera.camTexel.y };
    };
    // Haze is a smooth screen-space overlay, so it is switched off for an exact comparison.
    pipeline.setGrade({ ...activeGrade, hazeAmount: 0 }, world.palette.haze);
    const a = shoot(0, 0);
    const b = shoot(dx, dz);
    world.setFocus(0, 0);
    pipeline.setGrade(activeGrade, world.palette.haze);
    const W = 642;
    const H = 362;
    const sx = b.tx - a.tx;
    const sy = b.ty - a.ty;
    let compared = 0;
    let different = 0;
    for (let y = 2; y < H - 2; y++) {
      for (let x = 2; x < W - 2; x++) {
        const ax = x + sx;
        const ay = y + sy;
        if (ax < 2 || ay < 2 || ax >= W - 2 || ay >= H - 2) continue;
        const i = (ay * W + ax) * 4;
        const j = (y * W + x) * 4;
        compared++;
        if (a.pixels[i] !== b.pixels[j] || a.pixels[i + 1] !== b.pixels[j + 1] || a.pixels[i + 2] !== b.pixels[j + 2]) different++;
      }
    }
    return { shiftTexels: [sx, sy], compared, different, percent: +((different / Math.max(1, compared)) * 100).toFixed(3) };
  },
};
}
